import { useEffect, useRef, useCallback, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import mermaid from 'mermaid';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';

// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'neutral',
  securityLevel: 'loose',
  fontFamily: 'Inter, sans-serif',
});

// Code block component with copy functionality
const CodeBlock = memo(({ children, className, ...props }) => {
  const [copied, setCopied] = useState(false);
  const codeRef = useRef(null);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  
  const handleCopy = useCallback(async () => {
    const code = codeRef.current?.textContent || '';
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  // Check if it's a mermaid diagram
  if (language === 'mermaid') {
    return <MermaidDiagram code={String(children).replace(/\n$/, '')} />;
  }

  return (
    <div className="relative group my-6 rounded-xl overflow-hidden shadow-lg">
      {language && (
        <div className="flex items-center justify-between px-4 py-2.5 text-xs font-medium text-gray-300 bg-slate-900 border-b border-slate-700">
          <span className="uppercase tracking-wide">{language}</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1 text-gray-400 transition-colors rounded-md hover:text-white hover:bg-slate-700"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-green-400" />
                <span className="text-green-400">Copié!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span>Copier</span>
              </>
            )}
          </button>
        </div>
      )}
      <pre className={`${language ? 'rounded-t-none' : 'rounded-xl'} !m-0 !p-0 bg-slate-900`}>
        <code ref={codeRef} className={`${className} ${language ? 'rounded-t-none' : 'rounded-xl'}`} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
});

CodeBlock.displayName = 'CodeBlock';

// Mermaid diagram component
const MermaidDiagram = memo(({ code }) => {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!code) return;
      
      try {
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        const { svg } = await mermaid.render(id, code);
        setSvg(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        setError('Erreur de rendu du diagramme');
      }
    };

    renderDiagram();
  }, [code]);

  if (error) {
    return (
      <div className="p-4 my-4 text-red-600 bg-red-50 border border-red-200 rounded-lg">
        {error}
        <pre className="mt-2 text-sm text-gray-600 overflow-x-auto">{code}</pre>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="flex justify-center my-8 p-4 bg-gray-50 rounded-xl overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
});

MermaidDiagram.displayName = 'MermaidDiagram';

// Custom image component
const ImageComponent = memo(({ src, alt, ...props }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 my-6 bg-gray-100 rounded-xl">
        <span className="text-gray-500">Image non disponible: {alt}</span>
      </div>
    );
  }

  return (
    <figure className="my-8">
      <div className={`relative rounded-xl overflow-hidden ${!loaded ? 'animate-pulse bg-gray-200 min-h-[200px]' : ''}`}>
        <img
          src={src}
          alt={alt}
          className={`w-full rounded-xl transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          loading="lazy"
          {...props}
        />
      </div>
      {alt && (
        <figcaption className="mt-3 text-center text-sm text-gray-500 italic">
          {alt}
        </figcaption>
      )}
    </figure>
  );
});

ImageComponent.displayName = 'ImageComponent';

// Main MarkdownRenderer component
export default function MarkdownRenderer({ content }) {
  const components = {
    // Code blocks
    code({ node, inline, className, children, ...props }) {
      if (inline) {
        return (
          <code className="px-1.5 py-0.5 text-sm font-mono bg-gray-100 text-indigo-600 rounded" {...props}>
            {children}
          </code>
        );
      }
      return <CodeBlock className={className} {...props}>{children}</CodeBlock>;
    },
    
    // Images
    img: ImageComponent,
    
    // Headings with anchor links
    h1: ({ children, id, ...props }) => (
      <h1 id={id} className="group text-4xl font-bold text-gray-900 mt-12 mb-6 scroll-mt-24" {...props}>
        {children}
        {id && <a href={`#${id}`} className="ml-2 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">#</a>}
      </h1>
    ),
    h2: ({ children, id, ...props }) => (
      <h2 id={id} className="group text-3xl font-bold text-gray-900 mt-10 mb-4 scroll-mt-24" {...props}>
        {children}
        {id && <a href={`#${id}`} className="ml-2 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">#</a>}
      </h2>
    ),
    h3: ({ children, id, ...props }) => (
      <h3 id={id} className="group text-2xl font-semibold text-gray-900 mt-8 mb-3 scroll-mt-24" {...props}>
        {children}
        {id && <a href={`#${id}`} className="ml-2 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">#</a>}
      </h3>
    ),
    h4: ({ children, id, ...props }) => (
      <h4 id={id} className="group text-xl font-semibold text-gray-800 mt-6 mb-2 scroll-mt-24" {...props}>
        {children}
        {id && <a href={`#${id}`} className="ml-2 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">#</a>}
      </h4>
    ),
    
    // Paragraphs
    p: ({ children, ...props }) => (
      <p className="text-lg text-gray-700 leading-relaxed mb-6" {...props}>
        {children}
      </p>
    ),
    
    // Lists
    ul: ({ children, ...props }) => (
      <ul className="list-disc list-outside ml-6 mb-6 space-y-2 text-gray-700" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol className="list-decimal list-outside ml-6 mb-6 space-y-2 text-gray-700" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li className="text-lg leading-relaxed pl-2" {...props}>
        {children}
      </li>
    ),
    
    // Blockquotes
    blockquote: ({ children, ...props }) => (
      <blockquote className="border-l-4 border-indigo-500 pl-6 py-2 my-6 bg-indigo-50/50 rounded-r-lg italic text-gray-700" {...props}>
        {children}
      </blockquote>
    ),
    
    // Tables
    table: ({ children, ...props }) => (
      <div className="overflow-x-auto my-8 rounded-xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead className="bg-gray-50" {...props}>
        {children}
      </thead>
    ),
    th: ({ children, ...props }) => (
      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" {...props}>
        {children}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td className="px-6 py-4 text-sm text-gray-700 border-t border-gray-100" {...props}>
        {children}
      </td>
    ),
    
    // Links
    a: ({ href, children, ...props }) => {
      const isExternal = href?.startsWith('http');
      return (
        <a
          href={href}
          className="text-indigo-600 hover:text-indigo-800 underline decoration-indigo-300 hover:decoration-indigo-500 transition-colors"
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          {...props}
        >
          {children}
        </a>
      );
    },
    
    // Horizontal rule
    hr: () => (
      <hr className="my-12 border-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
    ),
    
    // Strong and emphasis
    strong: ({ children, ...props }) => (
      <strong className="font-semibold text-gray-900" {...props}>
        {children}
      </strong>
    ),
    em: ({ children, ...props }) => (
      <em className="italic text-gray-700" {...props}>
        {children}
      </em>
    ),
  };

  return (
    <article className="prose-custom max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight, rehypeRaw, rehypeSlug]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </article>
  );
}
